import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { Company, companyApiClient } from '@src/api/tenant/company/companyApiClient';
import { Pagination } from '@src/api/types/pagination';
import PaginationComponent from '@src/components/Pagination';
import { useQueryParams } from '@src/hooks/useQueryParams';
import { TENANT_URL } from '@src/routes/tenant';
import Button from '@src/ui/elements/button/Button.tsx';
import InputSearch from '@src/ui/elements/form/InputSearch';
import StatusTip from '@src/ui/elements/table/StatusTip';
import Th from '@src/ui/elements/table/Th.tsx';
import Title from '@src/ui/elements/typography/Title.tsx';
import React, { useEffect, useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';

const CompanyListForm: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState<Pagination>();

  const navigate = useNavigate();

  const {
    searchValue,
    setSearchValue,
    handleSort,
    handlePageChange,
    handleSearch,
    handleSearchKeyDown,
    getSearchParams,
    sort,
    page,
    keyword,
    direction,
  } = useQueryParams({});

  useEffect(() => {
    if (!sort) {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('sort', 'created_at');
      newSearchParams.set('direction', 'desc');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [sort, searchParams, setSearchParams]);

  const loadCompanies = async () => {
    const params = getSearchParams();

    const res = await companyApiClient.index(params);
    handleApiResponse(res, (data) => {
      setCompanies(data.data);
      setPagination(data.pagination);
    });
  };

  useEffect(() => {
    loadCompanies();
  }, [sort, direction, page, keyword]);

  return (
    <main className=''>
      <div>
        <div className='flex flex-col'>
          <div className='flex justify-between gap-4'>
            <div className='pt-2'>
              <Title>企業一覧</Title>
            </div>
            <div className='w-[250px]'>
              <InputSearch
                value={searchValue}
                defaultValue={keyword || ''}
                onChange={(e) => setSearchValue(e.target.value)}
                onSubmit={handleSearch}
                onKeyDown={handleSearchKeyDown}
                onClear={() => {
                  setSearchValue('');
                  setSearchParams(new URLSearchParams());
                }}
              />
            </div>
          </div>
        </div>
        {keyword && (
          <p className='overflow-wrap-anywhere mt-8 text-sm'>
            「{keyword}」で絞り込んだ結果：{pagination?.total}件
          </p>
        )}
        <div className='mt-8 overflow-auto'>
          <table className='size-full min-w-[900px] table-fixed text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
            <colgroup>
              <col className='w-[100px]' /> {/* 企業ID */}
              <col /> {/* 企業名 */}
              <col className='w-[140px]' /> {/* 代表者名 */}
              <col className='w-[140px]' /> {/* 登録日 */}
              <col className='w-[140px]' /> {/* ステータス */}
              <col className='w-[140px]' /> {/* 操作 */}
            </colgroup>
            <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
              <tr>
                <Th
                  sortable
                  sortOrder={sort === 'id' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('id')}
                >
                  企業ID
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'name_kana' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('name_kana')}
                >
                  企業名
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'fullname_kana' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('fullname_kana')}
                >
                  代表者名
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'created_at' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('created_at')}
                >
                  登録日
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'enabled' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('enabled')}
                >
                  ステータス
                </Th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {companies.length > 0
                ? companies.map((company) => (
                    <tr
                      className='[&:nth-child(2n)]:bg-gray-10'
                      key={company.id}
                    >
                      <td>
                        <div className='truncate px-3 py-6'>{company.id}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{company.name}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>
                          {company.family_name} {company.first_name}
                        </div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{company.created_on}</div>
                      </td>
                      <td>
                        <div className='px-3 py-6'>
                          <StatusTip
                            label={company.status ? '有効' : '無効'}
                            active={company.status}
                          />
                        </div>
                      </td>
                      <td>
                        <div className='flex justify-end px-4'>
                          <Button
                            size='s'
                            variant='quaternary'
                            outline
                            onClick={() =>
                              navigate(generatePath(TENANT_URL.COMPANIES_EDIT, { id: company.id }))
                            }
                          >
                            編集する
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
          {!keyword && pagination?.total === 0 ? (
            <p className='mt-4 text-sm'>データがありません。</p>
          ) : null}
        </div>
        {pagination && pagination.total !== 0 && (
          <div className='mt-6'>
            <PaginationComponent
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </main>
  );
};

export default CompanyListForm;
